import { useDeck } from "../../../lib/deck-context";
import { fmtM, fmtMoney } from "../../../lib/model";
import { Card, Eyebrow, Note } from "../../deck/primitives";
import { MATRIX, MATRIX_COLS, PIPELINE, SRC } from "./data";
import { NumIn, Src, Stat, Tag } from "./ui";

const LABELS = { conservative: "Conservative", base: "Base", accelerated: "Accelerated" };
const POINTS = ["Opening", "End Yr 1", "End Yr 2", "End Yr 3"];

const runLeaseUp = (p, a, m, rate) => {
  const ds = m.debt * (rate / 100);
  const years = [1, 2, 3].map((y) => {
    const occ = (p[y - 1] + p[y]) / 2;
    const rev = a.sf * a.rent * (occ / 100) + a.other * Math.min(occ / a.occ, 1);
    const noi = rev * (1 - a.exp / 100) - a.groundRent;
    return { occ, rev, noi, ds, gap: Math.min(noi - ds, 0) };
  });
  const stabIdx = p.findIndex((v, i) => i > 0 && v >= a.occ);
  const noiFull = (a.sf * a.rent * (p[3] / 100) + a.other * Math.min(p[3] / a.occ, 1)) * (1 - a.exp / 100) - a.groundRent;
  return { years, ds, cash: -years.reduce((t, y) => t + y.gap, 0), refi: stabIdx > 0 ? `Year ${stabIdx}` : "Beyond Year 3", value: noiFull / (a.cap / 100) };
};

const LeaseUpModel = () => {
  const { market, setMarket, inputs, m } = useDeck();
  const res = Object.fromEntries(Object.entries(market.leaseUp).map(([k, p]) => [k, runLeaseUp(p, inputs, m, market.rate)]));
  const setPt = (k, i, v) => setMarket("leaseUp", { ...market.leaseUp, [k]: market.leaseUp[k].map((x, j) => (j === i ? v : x)) });
  const metric = (label, f) => (
    <tr key={label}><td className="text-steel">{label}</td>{Object.keys(res).map((k) => <td key={k} className={`text-right tabular-nums ${k === "base" ? "text-ink font-medium" : "text-char"}`}>{f(res[k])}</td>)}</tr>
  );
  return (
    <Card className="p-7 lg:p-9">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><Eyebrow>Project Lease-Up Model</Eyebrow><div className="mt-2 font-display text-2xl text-ink">Murrieta Family Commons occupancy path</div></div>
        <div className="flex items-center gap-2 text-sm text-steel">Construction loan rate (interest-only) <NumIn testid="mk-leaseup-rate" value={market.rate} step={0.25} suffix="%" w="w-12" onChange={(v) => setMarket("rate", v)} /></div>
      </div>
      <div className="mt-7 grid md:grid-cols-3 gap-4">
        {Object.keys(market.leaseUp).map((k) => (
          <div key={k} data-testid={`mk-leaseup-${k}`} className={`rounded-xl border p-5 ${k === "base" ? "border-arq/50 bg-arq-soft" : "border-line"}`}>
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-arq">{LABELS[k]}</div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {POINTS.map((pt, i) => (
                <label key={pt} className="text-xs text-steel">{pt}<div className="mt-1"><NumIn testid={`mk-leaseup-${k}-${i}`} value={market.leaseUp[k][i]} suffix="%" w="w-12" onChange={(v) => setPt(k, i, v)} /></div></label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-7 overflow-x-auto">
        <table data-testid="mk-leaseup-table" className="data-table w-full min-w-[640px]">
          <thead><tr><th>Impact</th>{Object.keys(res).map((k) => <th key={k} className="!text-right">{LABELS[k]}</th>)}</tr></thead>
          <tbody>
            {[0, 1, 2].map((i) => metric(`Year ${i + 1} revenue / NOI`, (r) => `${fmtM(r.years[i].rev)} / ${fmtM(r.years[i].noi)}`))}
            {metric("Annual debt service (interest-only)", (r) => fmtM(r.ds))}
            {metric("Year 1 coverage (NOI ÷ debt service)", (r) => `${(r.years[0].noi / r.ds).toFixed(2)}×`)}
            {metric("Cash required to cover shortfalls (Yrs 1–3)", (r) => fmtMoney(r.cash))}
            {metric("Refinance timing (reaches stabilized occupancy)", (r) => r.refi)}
            {metric("Stabilized value at Year 3 occupancy", (r) => fmtM(r.value))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-steel">Year revenue uses the average of start/end occupancy; other revenue scales with occupancy. Debt = working construction debt ({fmtM(m.debt)}). Planning model only.</p>
    </Card>
  );
};

const STAGES = [["prospect", "Prospect"], ["loi", "Signed LOI"], ["negotiation", "Lease Negotiation"], ["executed", "Executed Lease"]];

const PreLeasing = () => {
  const { market, setMarket, inputs } = useDeck();
  const tot = (k) => market.pre.reduce((t, r) => t + r[k], 0);
  const executedPct = inputs.sf ? (tot("executed") / inputs.sf) * 100 : 0;
  return (
    <Card className="p-7 lg:p-9">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><Eyebrow>Tenant Pre-Leasing Target</Eyebrow><div className="mt-2 font-display text-2xl text-ink">Pre-lease before construction financing / major vertical commitment</div></div>
        <div className="flex items-center gap-2 text-sm text-steel">
          Target <NumIn testid="mk-pre-low" value={market.preLow} suffix="%" w="w-10" onChange={(v) => setMarket("preLow", v)} /> – <NumIn testid="mk-pre-high" value={market.preHigh} suffix="%" w="w-10" onChange={(v) => setMarket("preHigh", v)} />
        </div>
      </div>
      <div className="mt-6 overflow-x-auto">
        <table data-testid="mk-pre-table" className="data-table w-full min-w-[720px]">
          <thead><tr><th>Category (SF)</th>{STAGES.map(([, l]) => <th key={l} className="!text-right">{l}</th>)}</tr></thead>
          <tbody>
            {market.pre.map((r, i) => (
              <tr key={r.cat}>
                <td className="text-ink">{r.cat}</td>
                {STAGES.map(([k]) => (
                  <td key={k} className="text-right"><NumIn testid={`mk-pre-${i}-${k}`} value={r[k]} step={500} w="w-20" onChange={(v) => setMarket("pre", market.pre.map((x, j) => (j === i ? { ...x, [k]: v } : x)))} /></td>
                ))}
              </tr>
            ))}
            <tr className="bg-paper"><td className="font-medium text-ink">Total SF</td>{STAGES.map(([k]) => <td key={k} className="text-right tabular-nums">{tot(k).toLocaleString()}</td>)}</tr>
          </tbody>
        </table>
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-6">
        <div><div className="text-xs text-steel">Executed pre-leasing</div><div data-testid="mk-pre-executed-pct" className="font-display text-4xl text-ink tabular-nums">{executedPct.toFixed(0)}%</div></div>
        <div className="flex-1 min-w-[240px]">
          <div className="relative h-3 rounded-full bg-paper overflow-hidden">
            <div className="absolute inset-y-0 bg-arq/25" style={{ left: `${market.preLow}%`, width: `${Math.max(market.preHigh - market.preLow, 0)}%` }} />
            <div className="absolute inset-y-0 left-0 bg-arq rounded-full" style={{ width: `${Math.min(executedPct, 100)}%` }} />
          </div>
          <div className="mt-2 text-xs text-steel">Only executed leases count toward the target. Prospects, LOIs and negotiations are tracked separately and are not treated as leasing.</div>
        </div>
      </div>
    </Card>
  );
};

export const TabLeaseUp = () => (
  <div className="space-y-6">
    <div className="grid lg:grid-cols-2 gap-5">
      <Note tone="accent"><strong>Net Absorption</strong> measures the change in occupied space during a period. Positive absorption means the market occupied more space than it vacated.</Note>
      <Note><strong>Leasing Activity</strong> measures signed lease transactions and is not the same thing as net absorption.</Note>
    </div>
    <div className="grid md:grid-cols-3 gap-5">
      <Stat testid="mk-abs-q3" label="Inland Empire Q3 2026 net absorption" value="+402,499 SF" tag="regional" src={SRC.ie} type="Net absorption" />
      <Stat testid="mk-abs-ytd" label="Inland Empire 2026 YTD net absorption" value="+1.245M SF" tag="regional" src={SRC.ie} type="Net absorption" />
      <Stat testid="mk-leasing-murrieta" label="Murrieta trailing 12-month leasing activity" value="~521K SF" sub="Leasing activity — not net absorption" tag="local" src={SRC.murrieta} type="Leasing activity" />
    </div>
    <LeaseUpModel />
    <PreLeasing />
  </div>
);

export const TabSupply = () => {
  const dot = (v) => (v === 2 ? <span className="inline-block h-3 w-3 rounded-full bg-arq" /> : v === 1 ? <span className="inline-block h-3 w-3 rounded-full border-2 border-arq bg-[linear-gradient(90deg,#2D6E6A_50%,transparent_50%)]" /> : <span className="text-line">—</span>);
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-5">
        <Stat testid="mk-supply-ie" label="Inland Empire retail under construction" value="~1.10M SF" tag="regional" src={SRC.ie} type="Pipeline" />
        <Card data-testid="mk-supply-competition" className="lg:col-span-2 p-7">
          <div className="text-xs text-steel">Competition level to Murrieta Family Commons</div>
          <div className="mt-2 font-display text-4xl text-ink">Low–Moderate</div>
          <p className="mt-3 text-sm text-char/80">The South Riverside pipeline is conventional, freeway-oriented big-box / drive-through commercial in Wildomar. Murrieta Family Commons is a downtown family destination with food, experiential and civic / public-realm uses. They compete for some restaurant and entertainment spending, but are not the same product.</p>
        </Card>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        {PIPELINE.map((p) => (
          <Card key={p.id} data-testid={`mk-supply-${p.id}`} className="p-7">
            <div className="flex flex-wrap items-start justify-between gap-3"><div><div className="font-display text-2xl text-ink">{p.name}</div><div className="text-xs text-steel mt-1">{p.sub}</div></div><Tag t="local" label={`${p.city} · ${p.cityTag}`} /></div>
            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[["Size", p.size], ["Delivery", p.delivery], ["Tenant / use mix", p.mix], ["Food component", p.food], ["Entertainment", p.ent], ["Competes directly?", "No — partial overlap on food spending"]].map(([k, v]) => (
                <div key={k}><div className="text-xs text-steel">{k}</div><div className="text-ink">{v}</div></div>
              ))}
            </div>
            {p.note && <Note className="mt-5">{p.note}</Note>}
            <Src s={{ source: p.source, date: "2026", geo: "Wildomar · South Riverside", updated: "Oct 2026" }} type="Pipeline record" />
          </Card>
        ))}
      </div>
      <Card className="overflow-x-auto">
        <div className="px-7 pt-7 pb-3"><Eyebrow>Competitive Differentiation</Eyebrow><div className="mt-2 font-display text-2xl text-ink">Not simply another strip center</div></div>
        <table data-testid="mk-diff-matrix" className="data-table w-full min-w-[980px]">
          <thead><tr><th>Feature</th>{MATRIX_COLS.map((c, i) => <th key={c} className={`!text-center ${i === 0 ? "!text-arq" : ""}`}>{c}</th>)}</tr></thead>
          <tbody>
            {MATRIX.map(([f, ...vals]) => (
              <tr key={f}><td className="text-ink">{f}</td>{vals.map((v, i) => <td key={i} className={`text-center ${i === 0 ? "bg-arq-soft" : ""}`}>{dot(v)}</td>)}</tr>
            ))}
          </tbody>
        </table>
        <p className="px-7 pb-6 pt-3 text-xs text-steel">● Core component · ◐ Sometimes / partial · — Typically absent. Arqitek qualitative assessment of typical formats.</p>
      </Card>
    </div>
  );
};
